import type { BaseElement } from 'slate';

import type { ParagraphChildElement } from './AlineElement';
import type { ListLevel } from './List';
import type { LocationIdProps } from './location-id';
import type { TableBorder } from './Table';
import type { UUID } from '../../../types/type-helpers';
import type { AlignmentType, ParagraphStyleVariant, Shade } from '../GlobalStyles';
import type { SuggestedParagraphEdit, SuggestedParagraphGlyphEdit } from '../suggested-edits';
import type { AlineTextFormats } from '../text';

export type ParagraphType = 'paragraph';
export const paragraphType: ParagraphType = 'paragraph';

/**
 * `between` (OOXML `w:between`) is drawn between two adjacent paragraphs with
 * identical border settings, replacing their shared top/bottom edges
 * (ECMA-376 §17.3.1.24).
 */
export type ParagraphBorderPosition = 'top' | 'bottom' | 'left' | 'right' | 'between';
export type ParagraphBorders = Partial<Record<ParagraphBorderPosition, Partial<TableBorder>>>;

/**
 * A partial set of paragraph props used to express an edit or suggestion intent.
 * Unlike the stored `ParagraphElement.listProps`, `listProps` may be `null` here
 * to request clearing a paragraph's list membership.
 */
export type PartialParagraphProps = Partial<Omit<ParagraphProps, 'listProps'>> & {
  listProps?: ParagraphListProps | null;
};
export type ParagraphProps = Omit<ParagraphElement, 'children'>;

export type ParagraphFormats = Pick<
  ParagraphElement,
  | 'alignment'
  | 'indentationFirstLine'
  | 'indentationLeft'
  | 'indentationRight'
  | 'lineSpacing'
  | 'spacingAfter'
  | 'spacingBefore'
  | 'variant'
>;

export interface ParagraphListProps {
  /** Identifies the list the paragraph belongs to. */
  listId: UUID;
  /** Indicates list level if the paragraph is a list item. Level is 0-based. */
  listLevel: ListLevel;
}

export interface ParagraphElement extends BaseElement, LocationIdProps {
  alignment: AlignmentType;
  children: ParagraphChildElement[];
  /* First line indent *in inches* relative to indentationLeft position. */
  indentationFirstLine: number;
  /* Paragraph left indentation *in inches*. */
  indentationLeft: number;
  /* Paragraph right indentation *in inches*. */
  indentationRight: number;
  /* Line spacing as a unit or multiplier - e.g., 1, 1.5, 2. */
  lineSpacing: number;
  /* Spacing after a paragraph *in points*. */
  spacingAfter: number;
  /* Spacing before a paragraph *in points*. */
  spacingBefore: number;
  type: ParagraphType;
  variant: ParagraphStyleVariant;
  /**
   * Optional paragraph borders (from OOXML `w:pBdr`).
   * Only present sides are set — missing sides mean no border.
   */
  borders?: ParagraphBorders;
  /**
   * When true, spacing before/after is suppressed between adjacent paragraphs
   * that share the same style (OOXML `w:contextualSpacing`).
   */
  contextualSpacing?: boolean;
  /**
   * Text formats for the paragraph glyph ("marker").
   *
   * @ref Section 17.3.1.29 of the ECMA-376 spec
   * @url http://officeopenxml.com/WPstyleParStyles.php
   */
  glyphProps?: Partial<AlineTextFormats> & {
    suggestedEdits?: SuggestedParagraphGlyphEdit[] | null;
  };
  /* @deprecated */
  listLevel?: ListLevel | null;
  listProps?: ParagraphListProps;
  shade?: Shade;
  suggestedEdits?: SuggestedParagraphEdit[] | null;
}
